export type ProfitPercentReason = 'zero-cost' | 'non-finite';

export interface ProfitPercentResult {
  profitPercent: number | null;
  profitPercentReason: ProfitPercentReason | null;
}

/** Return on absolute net entry cost, not collateral or return on risk. */
export function calculateReturnOnCost({
  profit,
  netEntryCost,
}: {
  profit: number;
  netEntryCost: number;
}): ProfitPercentResult {
  if (netEntryCost === 0) {
    return { profitPercent: null, profitPercentReason: 'zero-cost' };
  }
  if (!Number.isFinite(profit) || !Number.isFinite(netEntryCost)) {
    return { profitPercent: null, profitPercentReason: 'non-finite' };
  }
  const profitPercent = (profit / Math.abs(netEntryCost)) * 100;
  return Number.isFinite(profitPercent)
    ? { profitPercent, profitPercentReason: null }
    : { profitPercent: null, profitPercentReason: 'non-finite' };
}

export function profitPercentUnavailableMessage(reason: ProfitPercentReason | null): string {
  return reason === 'zero-cost'
    ? 'Return on cost unavailable: net entry cost is zero.'
    : 'Return on cost unavailable: inputs or result are not finite.';
}

export type MaxRiskPercentReason = 'zero-risk' | 'unavailable-risk' | 'invalid-risk' | 'non-finite';

export interface MaxRiskPercentResult {
  percentOfMaxRisk: number | null;
  percentOfMaxRiskReason: MaxRiskPercentReason | null;
}

/** Display return on maximum risk; maxLoss is the existing signed loss, not a positive cost. */
export function calculateReturnOnMaxRisk({
  profit,
  maxLoss,
}: {
  profit: number;
  maxLoss: number | null | undefined;
}): MaxRiskPercentResult {
  if (maxLoss == null || !Number.isFinite(maxLoss)) {
    return { percentOfMaxRisk: null, percentOfMaxRiskReason: 'unavailable-risk' };
  }
  if (maxLoss === 0) return { percentOfMaxRisk: null, percentOfMaxRiskReason: 'zero-risk' };
  if (maxLoss > 0) return { percentOfMaxRisk: null, percentOfMaxRiskReason: 'invalid-risk' };
  const { profitPercent } = calculateReturnOnCost({ profit, netEntryCost: maxLoss });
  return {
    percentOfMaxRisk: profitPercent,
    percentOfMaxRiskReason: profitPercent === null ? 'non-finite' : null,
  };
}

export function maxRiskPercentUnavailableMessage(reason: MaxRiskPercentReason | null): string {
  switch (reason) {
    case 'zero-risk':
      return 'Return on maximum risk unavailable: maximum loss is zero.';
    case 'unavailable-risk':
      return 'Return on maximum risk unavailable: maximum loss is unavailable or unbounded.';
    case 'invalid-risk':
      return 'Return on maximum risk unavailable: maximum loss must be a negative loss amount.';
    default:
      return 'Return on maximum risk unavailable: profit or the resulting ratio is not finite.';
  }
}
